import * as React from 'react'
import moment from 'moment'
import type { Moment } from 'moment'
import { addDays, format } from 'date-fns'
import DailyNavigation from '../components/navigations/DailyNavigation'
import useVehicleActivityService from '../../../services/ActivityService'
import { useDate } from '@/modules/drivers/stores/dateStore'
import { useActivity } from '@/modules/vehicles/stores/activityStore'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
import Timeline from '@/modules/drivers/tabs/activities/components_new/Timeline'
import { TimelineProps } from '@/modules/drivers/tabs/activities/components_new/timelineProps'
import ActivityList from '../components/lists/ActivityList'
import { ActivityModel } from '@/app/models'
import { Slot } from '@/app/constants/slot'
import { EventModel } from '@/app/models/newDelegation/eventModel'
import { TmsDataModel } from '@/app/models/tmsDataModel'
import { CardInsertModel } from '@/app/models/cardInsertModel'
import { PlaceModel } from '@/app/models/placeModel'
import { DayActivityModel } from '@/app/models/dayActivityModel'
import { ViolationModel } from '@/app/models'
import { DailyRestEventModel } from '@/app/models/dailyRestEventModel'
import { DailyPartialRegularRestEventModel } from '@/app/models/dailyPartialRegularRestModel'
import { WeeklyRestEventModel } from '@/app/models/weeklyRestModel'
import { WeeklyReducedRestEventModel } from '@/app/models/weeklyRegularRestModel'
import { BreakModel } from '@/app/models/breakModel'
import { PartialBreakModel } from '@/app/models/partialBreakModel'
import { WeekRecompenseModel } from '@/app/models/weekRecompenseModel'
import { OutOfScopeModel } from '@/app/models/outOfScopeModel'
import { MinimalPaymentCountryModel } from '@/app/models/minimalPaymentCountryModel'
import { ExistingDelegationModel } from '@/app/models/existingDelegationModel'
import { BaseEventModel } from '@/app/models/baseEventModel'
import { useNavigate } from 'react-router-dom'
import { useTypedParams } from 'react-router-typesafe-routes/dom'
import { VEHICLE_ROUTES } from '../../../routes-typed'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

import FlexView from '@/modules/shared/components/FlexView'
import { TabContent, TabPane } from '@/modules/shared/components/Tabs'
import { LoaderIcon } from '@/app/features/icons/Loader'
import ActivityTooltipRenderer from '@/modules/drivers/tabs/activities/components/tooltips/renderers/ActivityTooltipRenderer'
import EventTooltipRenderer from '@/modules/drivers/tabs/activities/components/tooltips/renderers/EventTooltipRenderer'
import WeeklyRestTooltipRenderer from '@/modules/drivers/tabs/activities/components/tooltips/renderers/WeeklyRestTooltipRenderer'
import PlaceItemToolTipRenderer from '@/modules/drivers/tabs/activities/components/tooltips/renderers/PlaceItemTooltipRenderer'
import WeekRecompenseTooltipRenderer from '@/modules/drivers/tabs/activities/components/tooltips/renderers/WeekecompenseTooltipRenderer'
import TmsDataItemTooltipRenderer from '@/modules/drivers/tabs/activities/components/tooltips/renderers/TmsDataItemTooltipRenderer'
import ExistingDelegationItemTooltipRenderer from '@/modules/drivers/tabs/activities/components/tooltips/renderers/ExistingDelegationItemTooltipRenderer'
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker'
import VehicleActivityContextMenu, {
  VehicleActivityMenuState,
} from '../components/VehicleActivityContextMenu'
import EditVehicleActivityModal from '../../../modals/activities/EditVehicleActivityModal'

const HEIGHT = 130
export const TABS = {
  ActivitiesSlot1: 'activitiesSlot1',
  ActivitiesSlot2: 'activitiesSlot2',
}

const Daily = () => {
  // Typed URL params — { companyId: number, vehicleId: number, date: string }.
  const { companyId, vehicleId, date: currentDate } = useTypedParams(
    VEHICLE_ROUTES.dailyActivity,
  )
  const navigate = useNavigate()
  const loading = useLoadingStore(selectLoading)
  const startLoading = useLoadingStore((s) => s.startLoading)
  const { setType } = useActivity()
  const { getVehicleActivity } = useVehicleActivityService()
  const { setSingle, single, formatApiDate } = useDate()
  const { t } = useLocalize()
  const can = usePermissionChecker(companyId)

  // Bumped after a chart is edited from the activity list, to refetch the day.
  const [refreshKey, setRefreshKey] = React.useState(0)
  const [activityMenu, setActivityMenu] =
    React.useState<VehicleActivityMenuState | null>(null)
  const [editedActivity, setEditedActivity] =
    React.useState<ActivityModel | null>(null)
  const closeActivityMenu = React.useCallback(() => setActivityMenu(null), [])
  const [allActivities, setAllActivities] = React.useState<ActivityModel[]>([])
  const [activitiesSlot1, setActivitiesSlot1] = React.useState<ActivityModel[]>(
    [],
  )
  const [activitiesSlot2, setActivitiesSlot2] = React.useState<ActivityModel[]>(
    [],
  )
  const [violations, setViolations] = React.useState<ViolationModel[]>([])
  const [places, setPlaces] = React.useState<PlaceModel[]>([])
  const [cardInserts, setCardInserts] = React.useState<CardInsertModel[]>([])
  const [dayActivities, setDayActivities] = React.useState<DayActivityModel[]>(
    [],
  )
  const [dailyRests, setDailyRests] = React.useState<DailyRestEventModel[]>([])
  const [dailyPartialRegularRests, setDailyPartialRegularRests] =
    React.useState<DailyPartialRegularRestEventModel[]>([])
  const [weeklyRests, setWeeklyRests] = React.useState<WeeklyRestEventModel[]>(
    [],
  )
  const [weeklyReducedRests, setWeeklyReducedRests] = React.useState<
    WeeklyReducedRestEventModel[]
  >([])
  const [breaks, setBreaks] = React.useState<BreakModel[]>([])
  const [partialBreaks, setPartialBreaks] = React.useState<PartialBreakModel[]>(
    [],
  )
  const [weekRecompenses, setWeekRecompenses] = React.useState<
    WeekRecompenseModel[]
  >([])
  const [outOfScopes, setOutOfScopes] = React.useState<OutOfScopeModel[]>([])
  const [changeAreas, setChangeAreas] = React.useState<BaseEventModel[]>([])
  const [tripEvents, setTripEvents] = React.useState<EventModel[]>([])
  const [delegationMarkers, setDelegationMarkers] = React.useState<
    EventModel[]
  >([])
  const [minimalPaymentCountries, setMinimalPaymentCountries] = React.useState<
    MinimalPaymentCountryModel[]
  >([])
  const [tmsData, setTmsData] = React.useState<TmsDataModel[]>([])
  const [existingDelegations, setExistingDelegations] = React.useState<
    ExistingDelegationModel[]
  >([])
  const [countries, setCountries] = React.useState<any[]>([])
  const [activeTab, setActiveTab] = React.useState(TABS.ActivitiesSlot1)
  const [chartWidth, setChartWidth] = React.useState(0)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const [dataLoaded, setDataLoaded] = React.useState(false)

  const [hourOffset, setHourOffset] = React.useState(0)

  const [hoveredActivity, setHoveredActivity] = React.useState<any>(null)
  const [hoverFromChart, setHoverFromChart] = React.useState(false)

  const handleActivityHoverFromChart = React.useCallback((activity: any) => {
    setHoveredActivity(activity)
    setHoverFromChart(true)
  }, [])

  const handleActivityHoverFromList = React.useCallback((activity: any) => {
    setHoveredActivity(activity)
    setHoverFromChart(false)
  }, [])

  const handleActivityLeave = React.useCallback(() => {
    setHoveredActivity(null)
    setHoverFromChart(false)
  }, [])

  const goToDate = (days: number) => {
    const [year, month, day] = currentDate.split('-').map(Number)
    const date = new Date(year, month - 1, day)

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', currentDate)
      return
    }


    setHourOffset(0)
    const nextDate = addDays(date, days)
    const next = format(nextDate, 'YYYY-MM-DD')

    navigate(
      VEHICLE_ROUTES.dailyActivity.buildPath({
        companyId,
        vehicleId,
        date: next,
      }),
    )
  }

  const goToHour = (hours: number) => {
    const nextOffset = hourOffset + hours
    const [year, month, day] = currentDate.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    if (isNaN(date.getTime())) return

    if (nextOffset >= 24) {
      const nextDate = format(addDays(date, 1), 'YYYY-MM-DD')
      setHourOffset(nextOffset - 24)
      navigate(
        VEHICLE_ROUTES.dailyActivity.buildPath({
          companyId,
          vehicleId,
          date: nextDate,
        }),
      )
    } else if (nextOffset < 0) {
      const prevDate = format(addDays(date, -1), 'YYYY-MM-DD')
      setHourOffset(nextOffset + 24)
      navigate(
        VEHICLE_ROUTES.dailyActivity.buildPath({
          companyId,
          vehicleId,
          date: prevDate,
        }),
      )
    } else {
      setHourOffset(nextOffset)
    }
  }

  const filterActivitiesBySlot = (
    allActivities: ActivityModel[],
    startDate: Moment,
    slot: Slot,
  ): ActivityModel[] => {
    if (!allActivities || allActivities.length === 0) return []

    const startTime = startDate
    const endTime = startDate.clone().add(1, 'days')

    const filtered = allActivities.filter((activity) => {
      // Filter by slot
      if (activity.slot !== slot) {
        return false
      }

      // Filter by date
      const activityStart = moment(activity.startTime)
      const activityEnd = moment(activity.endTime)

      if (activityEnd <= startTime || activityStart >= endTime) {
        return false
      }

      return true
    })

    const merged: ActivityModel[] = []
    for (let i = 0; i < filtered.length; i++) {
      const activity = filtered[i]

      if (merged.length > 0) {
        const last = merged[merged.length - 1]
        if (
          moment(last.endTime).isSame(moment(activity.startTime)) &&
          activity.type === last.type
        ) {
          last.endTime = activity.endTime
          continue
        }
      }

      merged.push({ ...activity })
    }

    return merged
  }

  const getDailyActivity = async () => {
    try {
      const dateObj = new Date(currentDate)
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date:', currentDate)
        setAllActivities([])
        return
      }

      const startDateAdjusted = addDays(dateObj, -1)
      const endDateAdjusted = addDays(dateObj, 2)
      const result = await getVehicleActivity({
        companyId,
        vehicleId,
        startTime: formatApiDate(startDateAdjusted),
        endTime: formatApiDate(endDateAdjusted),
        slot: Slot.Both,
      })
      const activities = (result?.activities ?? []).map((a) => ({
        ...a,
        isVehicleSource: true,
      }))
      const violations = result?.violations ?? []
      const places = result?.places ?? []
      const cardInserts = result?.cardInserts ?? []
      const dayActivities = result?.dayActivities ?? []
      const dailyRests = result?.dailyRests ?? []
      const dailyPartialRegularRests = result?.dailyPartialRegularRests ?? []
      const weeklyRests = result?.weeklyRests ?? []
      const weeklyReducedRests = result?.weeklyReducedRests ?? []
      const breaks = result?.breaks ?? []
      const partialBreaks = result?.partialBreaks ?? []
      const weekRecompenses = result?.weekRecompenses ?? []
      const outOfScopes = result?.outOfScopes ?? []
      const changeAreas = result?.changeAreas ?? []
      const tripEvents = result?.tripEvents ?? []
      const delegationMarkers = result?.delegationMarkers ?? []
      const minimalPaymentCountries = result?.minimalPaymentCountries ?? []
      const tmsData = result?.tmsData ?? []
      const existingDelegations = result?.existingDelegations ?? []
      const countries = result?.countries ?? []

      setAllActivities(activities)
      setViolations(violations)
      setPlaces(places)
      setCardInserts(cardInserts)
      setDayActivities(dayActivities)
      setDailyRests(dailyRests)
      setDailyPartialRegularRests(dailyPartialRegularRests)
      setWeeklyRests(weeklyRests)
      setWeeklyReducedRests(weeklyReducedRests)
      setBreaks(breaks)
      setPartialBreaks(partialBreaks)
      setWeekRecompenses(weekRecompenses)
      setOutOfScopes(outOfScopes)
      setChangeAreas(changeAreas)
      setTripEvents(tripEvents)
      setDelegationMarkers(delegationMarkers)
      setMinimalPaymentCountries(minimalPaymentCountries)
      setTmsData(tmsData)
      setExistingDelegations(existingDelegations)
      setCountries(countries)
      setDataLoaded(true)
    } catch (error) {
      console.error('Error fetching vehicle daily activity:', error)
      setAllActivities([])
      setDataLoaded(true)
    }
  }

  React.useEffect(() => {
    if (allActivities.length > 0 && currentDate) {
   
      const startDate = moment(currentDate).add(hourOffset, 'hours')
      const slot1Activities = filterActivitiesBySlot(
        allActivities,
        startDate,
        Slot.Driver,
      )
      const slot2Activities = filterActivitiesBySlot(
        allActivities,
        startDate,
        Slot.CoDriver,
      )
      setActivitiesSlot1(slot1Activities)
      setActivitiesSlot2(slot2Activities)
    } else {
      setActivitiesSlot1([])
      setActivitiesSlot2([])
    }
  }, [allActivities, currentDate, hourOffset])

  React.useEffect(() => {
    const updateWidth = () => {
      if (wrapperRef.current) {
        const width = wrapperRef.current.offsetWidth
        if (width > 0) {
          setChartWidth(width)
        }
      }
    }

    updateWidth()

    const observer = new ResizeObserver(() => {
      updateWidth()
    })

    if (wrapperRef.current) {
      observer.observe(wrapperRef.current)
    }

    return () => observer.disconnect()
  }, [dataLoaded])

  React.useEffect(() => {
    setType('daily')
  }, [])

  React.useEffect(() => {
    setSingle(currentDate)
    setDataLoaded(false)
    const stopLoading = startLoading()
    const timer = setTimeout(() => {
      getDailyActivity().finally(stopLoading)
    }, 500)
    return () => {
      clearTimeout(timer)
      stopLoading()
    }
  }, [currentDate, vehicleId, refreshKey])

  
  const startTime = moment(currentDate).add(hourOffset, 'hours')

  const windowEnd = startTime.clone().add(1, 'days')
  const filterByWindow = (
    items: ReadonlyArray<any> | undefined,
    endKey?: string,
  ): any[] => {
    if (!items) return []
    return items.filter((item: any) => {
      const end = moment(endKey ? item[endKey] : item.endTime)
      return !(end < startTime || moment(item.startTime) > windowEnd)
    })
  }
  const filterEventsByWindow = (
    items: ReadonlyArray<EventModel> | undefined,
  ): EventModel[] => {
    if (!items) return []
    return items.filter((item) => {
      const eventTime = moment(item.date)
      return !(eventTime < startTime || eventTime > windowEnd)
    })
  }
  const filterTmsByWindow = (
    items: ReadonlyArray<TmsDataModel> | undefined,
  ): TmsDataModel[] => {
    if (!items) return []
    return items.filter((item) => {
      const end = moment(item.unloadingDate)
      return !(end < startTime || moment(item.loadingDate) > windowEnd)
    })
  }

  const buildTimelineProps = (slotActivities: ActivityModel[]) =>
    ({
      startTime,
      days: 1,
      width: chartWidth,
      height: HEIGHT,
      extraCurrentDate: currentDate,
      translate: t,
      activities: slotActivities,
      places: filterByWindow(places),
      cardInserts: filterByWindow(cardInserts),
      dayActivities: filterByWindow(dayActivities),
      violations: filterByWindow(violations),
      dailyRests: filterByWindow(dailyRests, 'formalRestEndDate'),
      dailyPartialRegularRests: filterByWindow(
        dailyPartialRegularRests,
        'formalRestEndDate',
      ),
      weeklyRests: filterByWindow(weeklyRests),
      weeklyReducedRests: filterByWindow(weeklyReducedRests),
      breaks: filterByWindow(breaks, 'formalRestEndDate'),
      partialBreaks: filterByWindow(partialBreaks, 'formalRestEndDate'),
      weekRecompenses: filterByWindow(weekRecompenses, 'realRestEndTime'),
      outOfScopes: filterByWindow(outOfScopes),
      changeAreas: filterByWindow(changeAreas),
      existingDelegations: filterByWindow(existingDelegations),
      minimalPaymentCountries: filterByWindow(minimalPaymentCountries),
      tmsData: filterTmsByWindow(tmsData),
      countries,
      delegationMarkers: filterEventsByWindow(delegationMarkers),
      allEvents: delegationMarkers,
      allTripEvents: filterEventsByWindow(tripEvents),
      allPreviousTripEvents: tripEvents,
      periodSelections: [],
      selectedActivity: hoveredActivity,
      editedActivity: null,
      isAddingNew: false,
      canEdit: false,
      canGenerateAttestation: false,
      flattenChart: false,
      simpleMode: false,
      disableTooltips: true,
      showViolations: true,
      showRests: true,
      showRestParts: true,
      showDayActivities: true,
      showTrips: true,
      showDelegations: true,
      editMode: false,
      onSelected: handleActivityHoverFromChart,
      onContextMenuChanged: () => {},
      onActivityContextMenu: (activity: ActivityModel, x: number, y: number) =>
        setActivityMenu({ activity, x, y }),
      onAdd: () => {},
      onEdit: () => {},
      onViolationSelected: () => {},
      onPeriodSelectionsChanged: () => {},
    }) as unknown as TimelineProps

  const displayContent =
    dataLoaded && allActivities != null && allActivities.length > 0

  return (
    <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
      <DailyNavigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        currentDate={currentDate}
        goToDate={goToDate}
        onShiftHour={goToHour}
      />
      <div
        ref={wrapperRef}
        className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-white"
      >
        {loading || !currentDate ? (
          <div className="tw-grid tw-h-[500px] tw-place-items-center">
            <LoaderIcon className="tw-w-16 tw-animate-spin" />
          </div>
        ) : displayContent ? (
          <>
            <div className="tw-flex-shrink-0">
              {chartWidth > 0 && (
                <FlexView vAlignContent="center" hAlignContent="left" column>
                  {/* Slot 1 Chart */}
                  <FlexView
                    hAlignContent="left"
                    vAlignContent="center"
                    style={{ position: 'relative' }}
                  >
                    <FlexView column>
                      <Timeline
                        key={`slot1-${startTime.toISOString()}`}
                        {...buildTimelineProps(activitiesSlot1)}
                      />
                    </FlexView>
                  </FlexView>

                  {/* Slot 2 Chart */}
                  <FlexView
                    hAlignContent="left"
                    vAlignContent="center"
                    style={{ position: 'relative' }}
                  >
                    <FlexView column>
                      <Timeline
                        key={`slot2-${startTime.toISOString()}`}
                        {...buildTimelineProps(activitiesSlot2)}
                      />
                    </FlexView>
                  </FlexView>
                </FlexView>
              )}
            </div>

            {chartWidth > 0 && (
              <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
                <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId={TABS.ActivitiesSlot1}>
                      {activeTab === TABS.ActivitiesSlot1 && (
                        <ActivityList
                          activities={activitiesSlot1}
                          onSelected={handleActivityHoverFromList}
                          hoveredActivity={hoveredActivity}
                          hoverFromChart={hoverFromChart}
                          onActivityLeave={handleActivityLeave}
                          onEdit={() => {}}
                          companyId={companyId}
                          onChartSaved={() => setRefreshKey((k) => k + 1)}
                        />
                      )}
                    </TabPane>
                    <TabPane tabId={TABS.ActivitiesSlot2}>
                      {activeTab === TABS.ActivitiesSlot2 && (
                        <ActivityList
                          activities={activitiesSlot2}
                          onSelected={handleActivityHoverFromList}
                          hoveredActivity={hoveredActivity}
                          hoverFromChart={hoverFromChart}
                          onActivityLeave={handleActivityLeave}
                          onEdit={() => {}}
                          companyId={companyId}
                          onChartSaved={() => setRefreshKey((k) => k + 1)}
                        />
                      )}
                    </TabPane>
                  </TabContent>
                </div>
              </div>
            )}
          </>
        ) : dataLoaded ? (
          <FlexView
            vAlignContent="center"
            hAlignContent="center"
            style={{ height: 300 }}
          >
            <span style={{ color: '#97A2B3', fontSize: 16 }}>
              {t('dailyActivities.vehicle-no-data')}
            </span>
          </FlexView>
        ) : null}
      </div>
      <ActivityTooltipRenderer translate={t} startTime={startTime} />
      <EventTooltipRenderer translate={t} startTime={startTime} />
      <WeeklyRestTooltipRenderer translate={t} startTime={startTime} />
      <PlaceItemToolTipRenderer translate={t} startTime={startTime} />
      <WeekRecompenseTooltipRenderer translate={t} startTime={startTime} />
      <TmsDataItemTooltipRenderer translate={t} startTime={startTime} />
      <ExistingDelegationItemTooltipRenderer
        translate={t}
        startTime={startTime}
      />
      {activityMenu && (
        <VehicleActivityContextMenu
          {...activityMenu}
          canEdit={can('canEditActivities')}
          onEdit={setEditedActivity}
          onClose={closeActivityMenu}
        />
      )}
      {editedActivity && (
        <EditVehicleActivityModal
          companyId={companyId}
          vehicleId={vehicleId}
          activity={editedActivity}
          close={() => setEditedActivity(null)}
          onSuccess={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  )
}

export default Daily
